import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateProject } from '../api/projects';

interface Props {
  onClose: () => void;
}

export default function AddProjectModal({ onClose }: Props) {
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [localPath, setLocalPath] = useState('');
  const [repoUrl, setRepoUrl] = useState('');
  // Static-mode default: TRUE. Most projects on a shared panel server
  // won't have their toolchain installed here, so static is the
  // expected case. Admin un-checks only when they've actually set up
  // PHP/Node/Python locally for this project.
  const [staticMode, setStaticMode] = useState(true);
  const createProject = useCreateProject();
  const navigate = useNavigate();

  return (
    <div
      className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-lg w-full p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold mb-1">Add project</h2>
        <p className="text-xs text-slate-500 mb-4">
          If the local path already exists on the server, it must contain{' '}
          <code className="font-mono">.pipeline/config.jsonc</code>. If it doesn&apos;t
          exist yet and you supply a <strong>Repo URL</strong>, the server will{' '}
          <code className="font-mono">git clone</code> it for you, then you&apos;ll
          still need to add the pipeline config separately.
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!slug.trim() || !name.trim() || !localPath.trim()) return;
            createProject.mutate(
              {
                slug: slug.trim(),
                name: name.trim(),
                local_path: localPath.trim(),
                ...(repoUrl.trim() ? { repo_url: repoUrl.trim() } : {}),
                static_mode: staticMode,
              },
              {
                onSuccess: (project) => {
                  onClose();
                  navigate(`/p/${project.slug}`);
                },
              },
            );
          }}
          className="space-y-3"
        >
          <Field label="Slug (URL-safe id)">
            <input
              required
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase())}
              placeholder="brisco"
              className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm font-mono"
            />
          </Field>
          <Field label="Display name">
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Brisco"
              className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm"
            />
          </Field>
          <Field label="Local path (absolute)">
            <input
              required
              value={localPath}
              onChange={(e) => setLocalPath(e.target.value)}
              placeholder="/var/www/html/Brisco-Workspace/brisco"
              className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm font-mono"
            />
          </Field>
          <Field label="Repo URL (used to auto-clone if local path is missing)">
            <input
              value={repoUrl}
              onChange={(e) => setRepoUrl(e.target.value)}
              placeholder="git@github.com:youraccount/brisco.git"
              className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm font-mono"
            />
            <p className="text-xs text-slate-500 mt-1">
              SSH URL recommended (uses the server&apos;s git identity). Leave blank if
              the folder already exists.
            </p>
          </Field>

          {/* ─── Project mode ────────────────────────────── */}
          <fieldset className="border border-slate-200 rounded p-3 space-y-2">
            <legend className="text-xs font-medium text-slate-700 px-1">
              How will this project run?
            </legend>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="run-mode"
                checked={staticMode}
                onChange={() => setStaticMode(true)}
                className="mt-0.5"
              />
              <span>
                <span className="font-medium">Static mode (recommended for shared servers)</span>
                <span className="block text-xs text-slate-500">
                  Pipeline edits files + commits + pushes to git. You pull the
                  branch locally to verify. No PHP / Node / Python needs to be
                  installed on this server.
                  {!localPath.includes('.pipeline') && (
                    <> Panel auto-writes a minimal <code className="font-mono">.pipeline/config.jsonc</code> for you.</>
                  )}
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="run-mode"
                checked={!staticMode}
                onChange={() => setStaticMode(false)}
                className="mt-0.5"
              />
              <span>
                <span className="font-medium">Full project (toolchain installed on this server)</span>
                <span className="block text-xs text-slate-500">
                  Pipeline runs <code className="font-mono">npm test</code> /{' '}
                  <code className="font-mono">php artisan</code> / etc. between steps.
                  Requires <code className="font-mono">.pipeline/config.jsonc</code> already
                  in the folder (run <code className="font-mono">pipeline init</code> first).
                </span>
              </span>
            </label>
          </fieldset>

          {createProject.error && (
            <div className="text-sm text-red-700">
              {(createProject.error as Error).message}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm rounded border border-slate-300 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createProject.isPending}
              className="px-3 py-1.5 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {createProject.isPending ? 'Connecting…' : 'Connect'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-700 block mb-1">{label}</span>
      {children}
    </label>
  );
}
